import moment from "moment";
import useTranslator from "../../hooks/useTranslator";
import CheckIcon0 from "../../assets/img/checkIcon02.svg";
import { getIdentityDetails, haveValue } from "../../helper/common";
import { isRegistryOfficerVerified } from "../../helper/kybReasons";
import { Avatar, Button, Card, Icon, Stat } from "../design";
import "./tenantInfoCards.css";

// Company login tenants: company data from Didit KYB (registry check) and
// the authorised signatories (KYB people with their own KYC). A tick is only
// shown for values from Didit that are approved (not in review).

const Field = ({ label, value, verified, className = "col-md-4 col-6" }) => (
  <div className={className}>
    <div className="tic-field">
      <div className="d-flex align-items-center gap-1">
        <p className="tic-field__label">{label}</p>
        {verified && haveValue(value) && (
          <img src={CheckIcon0} alt="" className="tic-check" />
        )}
      </div>
      <p className="tic-field__value">{haveValue(value) ? value : "-"}</p>
    </div>
  </div>
);

export const DetailsCompanyInfo = ({ user }) => {
  const { _t } = useTranslator();
  const company = user?.onfido_user_kyc?.kyb_data || {};
  const registryApproved = company.status === "Approved";
  const incorporationDate = moment(company.incorporation_date);

  return (
    <Card
      className="tic-card"
      title={
        <span className="tic-card__title">
          <Icon name="building" size={20} />
          {_t(
            "companyInformation",
            "Company profile: title of the card with the company data from the KYB registry check"
          )}
        </span>
      }
    >
      <div className="row">
        <Field
          label={_t("name")}
          value={company.company_name || user?.company_legal_name}
          verified={registryApproved && haveValue(company.company_name)}
        />
        <Field
          label={_t(
            "companyCif",
            "Company profile: label of the company tax id (CIF) field"
          )}
          value={company.registration_number || user?.company_cif}
          verified={
            registryApproved &&
            haveValue(company.registration_number) &&
            company.vat_validation_status === "valid"
          }
        />
        <Field
          label={_t(
            "incorporationDate",
            "Company profile: label of the company incorporation date field"
          )}
          value={
            company.incorporation_date && incorporationDate.isValid()
              ? incorporationDate.format("DD/MM/YYYY")
              : null
          }
          verified={registryApproved}
        />
        <Field
          label={_t(
            "legalForm",
            "Company profile: label of the company legal form field (e.g. limited company)"
          )}
          value={company.company_type ? _t(company.company_type) : null}
          verified={registryApproved}
        />
        <Field
          label={_t("address")}
          value={company.registered_address}
          verified={registryApproved}
          className="col-md-8"
        />
      </div>
    </Card>
  );
};

export const DetailsAuthorisedSignatories = ({ user }) => {
  const { _t } = useTranslator();
  const people = user?.onfido_user_kyc?.kyb_people || [];
  // Officers the company registry disclosed; shown even when they were not
  // submitted for KYC in the flow (Didit keeps the two lists apart).
  const officers = user?.onfido_user_kyc?.kyb_data?.officers || [];

  return (
    <Card
      className="tic-card"
      title={
        <span className="tic-card__title">
          <Icon name="idCard" size={20} />
          {_t(
            "authorisedSignatories",
            "Company profile: title of the card listing the people who sign for the company (KYB people)"
          )}
        </span>
      }
      description={_t(
        "authorisedSignatoriesDescription",
        "Company profile: subtitle of the authorised signatories card, the people who sign the lease for the company"
      )}
    >
      {officers.length > 0 && (
        <>
          <p className="tic-subheading">
            {_t(
              "registryOfficers",
              "Authorised signatories card: heading of the people found in the company registry by Didit"
            )}
          </p>
          {officers.map((officer, index) => (
            <div className="row" key={officer?.uuid || index}>
              <Field
                label={_t("name")}
                value={officer?.name}
                verified={isRegistryOfficerVerified(officer, user)}
              />
              <Field
                label={_t(
                  "officerPosition",
                  "Authorised signatories card: label of the position of a person found in the company registry"
                )}
                value={officer?.designation}
              />
            </div>
          ))}
        </>
      )}
      {people.length > 0 && (
        <p className="tic-subheading">
          {_t(
            "addedSignatories",
            "Authorised signatories card: heading of the people the company added in the KYB flow for KYC"
          )}
        </p>
      )}
      {people.map((person, index) => {
        const approved = person?.kyc_status === "approved";
        const identity = getIdentityDetails({}, person);
        const name =
          person?.kyc_data?.full_name ||
          [person?.kyc_data?.first_name, person?.kyc_data?.last_name]
            .filter(Boolean)
            .join(" ") ||
          person?.name;
        return (
          <div className="row" key={person?.party_uuid || index}>
            <Field
              label={_t("name")}
              value={name}
              verified={approved && haveValue(person?.kyc_data?.first_name)}
            />
            <Field
              label={_t(
                identity?.isForeignId
                  ? "documentNumber"
                  : identity?.type === "passport"
                    ? "passport"
                    : "dniNie"
              )}
              value={identity?.value}
              verified={approved && identity?.showTick}
            />
            <Field
              label={_t("nationality")}
              value={person?.nationality ? _t(person.nationality) : null}
              verified={approved && haveValue(person?.kyc_data?.nationality)}
            />
          </div>
        );
      })}
    </Card>
  );
};

// Beneficial owners from Skor Lite: the beneficial-ownership deed's document
// carries a `beneficialOwners` list (document analysis); every other document
// has null. Every owner on the deed is shown; a share the deed does not print
// (null) is shown as "-".
const getBeneficialOwners = (user) => {
  const reports =
    user?.tenant_extra_field_id?.document_analysis?.profileReports ?? [];
  const seen = new Set();
  const owners = [];
  reports.forEach((profile) => {
    const report = profile?.report;
    const documents = [
      ...(report?.documentBuckets ?? []).flatMap((b) => b?.documents ?? []),
      ...(report?.allDocuments ?? []),
    ];
    documents.forEach((doc) => {
      const list = doc?.beneficialOwners ?? doc?.data?.beneficialOwners;
      (Array.isArray(list) ? list : []).forEach((owner) => {
        const key = `${owner?.id_number || owner?.name}-${owner?.owned_company_name}`;
        if (seen.has(key)) return;
        seen.add(key);
        owners.push(owner);
      });
    });
  });
  return owners;
};

export const DetailsBeneficialOwners = ({ user }) => {
  const { _t } = useTranslator();
  const owners = getBeneficialOwners(user);
  if (!owners.length) return null;

  const companyName = user?.onfido_user_kyc?.kyb_data?.company_name;
  const describe = (owner) =>
    [
      owner?.holder_type === "legal_entity"
        ? _t(
            "ownerTypeCompany",
            "Beneficial owners card: line under an owner that is a company holding shares"
          )
        : _t(
            "ownerTypePerson",
            "Beneficial owners card: line under an owner that is a person"
          ),
      owner?.ownership_type ? _t(owner.ownership_type) : null,
      owner?.owned_company_name && owner.owned_company_name !== companyName
        ? owner.owned_company_name
        : null,
    ]
      .filter(Boolean)
      .join(" · ");

  return (
    <Card
      className="tic-card"
      title={
        <span className="tic-card__title">
          <Icon name="users" size={20} />
          {_t(
            "beneficialOwners",
            "Company profile: title of the card listing every beneficial owner from the beneficial-ownership deed"
          )}
        </span>
      }
    >
      <div className="tic-income-list">
        {owners.map((owner, index) => (
          <div
            key={`${owner?.id_number || owner?.name}-${index}`}
            className="tic-income-row"
          >
            <Avatar name={owner?.name} />
            <span className="tic-income-row__info">
              <span className="tic-income-row__value">
                <b>{owner?.name || "-"}</b>
              </span>
              <span className="tic-income-row__meta">{describe(owner)}</span>
            </span>
            <span className="tic-owner-share">
              {haveValue(owner?.ownership_percentage)
                ? `${owner.ownership_percentage}%`
                : "-"}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
};

// Company finances from the Skor Lite profile report: "Tax Returns" key factor
// → dataInJSON.taxReturns[] → company. Only the company the engine marks as
// the uploader (owner_type legal_entity, is_uploader true) is read, so another
// company in the files never shows here.
const getCompanyTaxReturns = (user) => {
  const profiles =
    user?.tenant_extra_field_id?.forensic_analysis?.profileReports ?? [];
  const own = profiles.find(
    (profile) =>
      profile?.data?.is_uploader === true &&
      profile?.data?.owner_type === "legal_entity"
  );
  const taxReturns =
    own?.report?.keyFactors?.keyFactors?.find(
      (factor) => factor?.keyFactor === "Tax Returns"
    )?.dataInJSON?.taxReturns ?? [];
  return taxReturns.filter((entry) => entry?.company);
};

const formatEuro = (value) =>
  `€${Math.round(Number(value)).toLocaleString("en-US")}`;

export const DetailsCompanyFinances = ({ user }) => {
  const { _t } = useTranslator();
  const taxReturns = getCompanyTaxReturns(user);
  const form200 = taxReturns.find((entry) =>
    haveValue(entry?.company?.plResult00500)
  );
  const form390 = taxReturns.find((entry) =>
    haveValue(entry?.company?.volumenOperaciones99)
  );
  if (!form200 && !form390) return null;

  return (
    <Card
      className="tic-card"
      title={
        <span className="tic-card__title">
          <Icon name="wallet" size={20} />
          {_t(
            "companyFinances",
            "Company profile (landlord): title of the card with the company's figures from its tax returns"
          )}
        </span>
      }
    >
      {form200 && (
        <>
          <p className="tic-field__label">
            {_t(
              "corporateTaxReturnModelo200",
              "Company finances card: heading of the Form 200 (corporate tax return) figures, followed by the year"
            )}
            {form200.taxReturnYear ? `, ${form200.taxReturnYear}` : ""}
          </p>
          <div className="ds-stat-row">
            <Stat
              label={_t(
                "netProfitForTheYear",
                "Company finances card: label of the net profit (Form 200 box 00500)"
              )}
              value={formatEuro(form200.company.plResult00500)}
            />
            <Stat
              label={_t(
                "averageMonthlyNetProfit",
                "Company finances card: label of the net profit divided by 12"
              )}
              value={formatEuro(form200.company.plResult00500 / 12)}
            />
          </div>
        </>
      )}
      {form390 && (
        <>
          <p className="tic-field__label">
            {_t(
              "annualVatSummaryModelo390",
              "Company finances card: heading of the Form 390 (annual VAT summary) figures, followed by the year"
            )}
            {form390.taxReturnYear ? `, ${form390.taxReturnYear}` : ""}
          </p>
          <div className="ds-stat-row">
            <Stat
              label={_t(
                "annualOperationsVolume",
                "Company finances card: label of the year's total operations volume (Form 390 box 99)"
              )}
              value={formatEuro(form390.company.volumenOperaciones99)}
            />
            <Stat
              label={_t(
                "averageMonthlyRevenue",
                "Company finances card: label of the operations volume divided by 12"
              )}
              value={formatEuro(form390.company.volumenOperaciones99 / 12)}
            />
          </div>
        </>
      )}
    </Card>
  );
};

// Landlord view: defaulter check for the company. Card only for now; the
// request is not wired yet.
export const DetailsDefaulterCheck = () => {
  const { _t } = useTranslator();
  return (
    <Card
      className="tic-card"
      title={
        <span className="tic-card__title">
          <Icon name="shield" size={20} />
          {_t(
            "defaulterCheck",
            "Company profile (landlord): title of the card to check the company in default registers"
          )}
        </span>
      }
      description={_t(
        "defaulterCheckDescription",
        "Company profile (landlord): text of the defaulter check card, the result shows at the top of the profile"
      )}
      actions={
        <Button size="md" disabled>
          {_t(
            "requestDefaulterCheck",
            "Company profile (landlord): button to request the defaulter check (not active yet)"
          )}
        </Button>
      }
    />
  );
};
